import { useCallback, useContext, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { DepositForAccount } from "@mezo/api"
import { API_ENDPOINT, SupportedTokenName } from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { useMutation } from "@tanstack/react-query"
import { Address } from "viem"
import { useAccount } from "wagmi"

import {
  depositBitcoin,
  depositToken,
  initializeLockFlow,
  initializeWithdrawalFlow,
  relockDeposit,
  RootState,
  selectActiveDeposits,
  selectActiveToken,
  selectCurrentLockForDeposit,
  selectDepositAmount,
  selectDepositFromAnotherWallet,
  selectIsConnected,
  selectLockTime,
  selectNonStablecoinDeposits,
  selectPortalStatistics,
  selectStablecoinDeposits,
  selectTokenBalance,
  selectTokenDecimals,
  selectWalletAddress,
  selectWithdrawalById,
  setDepositAmount,
  setDepositId,
  setHasDepositError,
  setOpenModal,
  updateDepositAutoMigration,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  BITCOIN_MIN_DEPOSIT,
  FRIENDLY_DECIMALS_AMOUNT,
} from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import {
  bigIntToFloat,
  calculateLockDuration,
  convertBitcoinToSatoshi,
  convertWeeksToSeconds,
  floatToBigInt,
  formatMats,
  getUnlockTime,
  isStablecoin,
  validateMaximumTokenAmount,
} from "#/shared/utils"

import { useEffectOnce } from "./custom"
import { useBitcoinRecoveryAddress } from "./deposit-another-wallet"
import { usePosthogDepositedEvent } from "./posthog-events"
import { useTokenUsdPrice } from "./token"
import { isPassportConnector } from "./wagmi"

export type DepositBoxProps = {
  id: string
  depositId: number | null
  userAmount: string
  amount: bigint
  token: SupportedTokenName
  lockStart: Date
  lockEnd: Date
  unlockTime: string | null
  dailyMats: number
  mintedAmount: bigint
  currentLockDuration: number
}

// Use only inside of 'useDeposit' hook
const useERC20Deposit = (): {
  depositERC20: () => Promise<boolean>
} => {
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const lockTime = useDappSelector(selectLockTime)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const dispatch = useDappDispatch()

  const depositHandler = useCallback(async () => {
    const lockupTime = lockTime
      ? convertWeeksToSeconds(lockTime.period)
      : undefined

    const parsedAmount = floatToBigInt(depositAmount, tokenDecimals)

    const { payload: deposit } = await dispatch(
      depositToken({
        amount: parsedAmount,
        token: activeToken,
        lockupTime,
      }),
    )

    if (!deposit) return false

    return true
  }, [dispatch, activeToken, tokenDecimals, lockTime, depositAmount])

  return { depositERC20: depositHandler }
}

// Use only inside of 'useDeposit' hook
const useBitcoinDeposit = () => {
  const depositAmount = useDappSelector(selectDepositAmount)
  const lockTime = useDappSelector(selectLockTime)

  const dispatch = useDappDispatch()
  const bitcoinRecoveryAddress = useBitcoinRecoveryAddress()

  const depositHandler = useCallback(async () => {
    if (!bitcoinRecoveryAddress) {
      return false
    }

    const lockPeriod = convertWeeksToSeconds(lockTime.period)
    const amountInSatoshi = convertBitcoinToSatoshi(depositAmount)

    const { payload: deposited } = await dispatch(
      depositBitcoin({
        lockPeriod,
        amount: Number(amountInSatoshi),
        bitcoinRecoveryAddress,
      }),
    )

    return !!deposited
  }, [dispatch, lockTime, depositAmount, bitcoinRecoveryAddress])

  return { depositBitcoin: depositHandler }
}

export const useDeposit = () => {
  const walletAddress = useDappSelector(selectWalletAddress)
  const activeToken = useDappSelector(selectActiveToken)
  const depositAmount = useDappSelector(selectDepositAmount)
  const lockTime = useDappSelector(selectLockTime)

  const { depositBitcoin: depositBitcoinHandler } = useBitcoinDeposit()
  const { depositERC20: depositERC20Handler } = useERC20Deposit()
  const { emitDepositedEvent } = usePosthogDepositedEvent()

  const { isBitcoinFlow } = useContext(FlowContext)

  const dispatch = useDappDispatch()

  const depositHandler = useCallback(async () => {
    if (!walletAddress) {
      dispatch(setHasDepositError(true))
      throw new Error("Please provide a wallet address.")
    }

    if (!depositAmount) {
      dispatch(setHasDepositError(true))
      throw new Error("Please provide a deposit amount.")
    }

    if (!lockTime) {
      dispatch(setHasDepositError(true))
      throw new Error("Please provide a deposit duration.")
    }

    // Based on the flow we invoke 'depositBitcoin' or 'depositERC20' function
    const deposited = isBitcoinFlow
      ? await depositBitcoinHandler()
      : await depositERC20Handler()

    dispatch(setHasDepositError(!deposited))

    if (deposited) {
      emitDepositedEvent(walletAddress, depositAmount, activeToken)
    }

    return deposited
  }, [
    depositBitcoinHandler,
    depositERC20Handler,
    isBitcoinFlow,
    lockTime,
    depositAmount,
    dispatch,
    emitDepositedEvent,
    walletAddress,
    activeToken,
  ])

  return { deposit: depositHandler }
}

export const useDepositActions = (
  id: string | null,
  depositId: number | null,
  token: SupportedTokenName | null,
  lockupTime: number,
) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const dispatch = useDappDispatch()

  const lockHandler = useCallback(async () => {
    const lockTimeInSeconds = convertWeeksToSeconds(lockupTime)

    if (!depositId || !token || !id) return false

    setIsProcessing(true)

    const { payload: lock } = (await dispatch(
      relockDeposit({
        id,
        depositId: BigInt(depositId),
        token,
        lockupTime: lockTimeInSeconds,
      }),
    )) as PayloadAction<boolean>

    setIsProcessing(false)

    return lock
  }, [id, depositId, token, lockupTime, dispatch])

  return {
    lock: lockHandler,
    isProcessing,
  }
}

export const useBitcoinBoost = (dailyMats: number) => {
  const btcDeposits = useDappSelector(selectNonStablecoinDeposits)
  const stablecoinDeposits = useDappSelector(selectStablecoinDeposits)
  const activeToken = useDappSelector(selectActiveToken)

  const bitcoinBoost = useMemo(() => {
    // Calculate total possible boost from stablecoins. It can be different from
    // the actual total boost, because user might not have enough BTC deposited
    // to fully the boost. In such case, his next BTC deposits will be boosted.
    const totalPossibleBoostFromStablecoins = stablecoinDeposits.reduce(
      (acc, deposit) => acc + deposit.totalDailyMats,
      0,
    )

    // The actual boost that is used.
    const currentBoostFromStablecoins = btcDeposits.reduce((acc, deposit) => {
      const depositDailyBoost = deposit.dailyBoostMats.reduce(
        (acc2, dailyBoost) => acc2 + parseInt(dailyBoost.value.toString(), 10),
        0,
      )

      return acc + depositDailyBoost
    }, 0)

    if (!isStablecoin(activeToken)) {
      // If the current boost is lower than total possible boost, then the next
      // non-stablecoin deposit will get the remaining boost.
      return totalPossibleBoostFromStablecoins > currentBoostFromStablecoins
        ? totalPossibleBoostFromStablecoins - currentBoostFromStablecoins
        : 0
    }

    // At this point we will calculate boost for new stablecoin deposit
    const totalBtcDailyMatsWithoutBoost = btcDeposits.reduce(
      (acc, deposit) => acc + deposit.dailyMats,
      0,
    )

    const notBoostedMatsFromBtcDeposits =
      totalBtcDailyMatsWithoutBoost > currentBoostFromStablecoins
        ? totalBtcDailyMatsWithoutBoost - currentBoostFromStablecoins
        : 0

    return Math.floor(Math.min(dailyMats, notBoostedMatsFromBtcDeposits))
  }, [btcDeposits, stablecoinDeposits, activeToken, dailyMats])

  return { bitcoinBoost }
}

export const useDepositInput = (initialTokenAmount?: string) => {
  const dispatch = useDappDispatch()
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const isConnected = useDappSelector(selectIsConnected)

  useEffectOnce(() => {
    if (initialTokenAmount || initialTokenAmount === "")
      dispatch(setDepositAmount(initialTokenAmount))
  })

  const setDepositTokenAmountCallback = useCallback(
    (tokenAmount: string) => {
      dispatch(setDepositAmount(tokenAmount))
    },
    [dispatch],
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const balance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, activeToken),
  )

  const depositFromAnotherWallet = useDappSelector(
    selectDepositFromAnotherWallet,
  )

  const maxTokenAmount = useMemo(
    () => (!depositFromAnotherWallet ? balance : undefined),
    [depositFromAnotherWallet, balance],
  )

  const checkValidBitcoinDepositAmount = useCallback(
    (input: string) => {
      if (activeToken !== "BTC" || !input) return true

      const parsedValue = Number(input)
      if (!parsedValue) return true

      return parsedValue >= BITCOIN_MIN_DEPOSIT
    },
    [activeToken],
  )

  const additionalValidationCallback = (input: string) => {
    const noError = {
      isValid: true,
      errorMessage: "",
    }

    if (!isConnected) {
      return noError
    }

    const isValidBitcoinInput = checkValidBitcoinDepositAmount(input)

    const isLowerOrEqualBalance = validateMaximumTokenAmount(
      floatToBigInt(input, tokenDecimals),
      balance,
    )

    if (!isValidBitcoinInput) {
      return {
        isValid: false,
        errorMessage: `Minimum deposit is ${BITCOIN_MIN_DEPOSIT} BTC`,
      }
    }

    if (!depositFromAnotherWallet && !isLowerOrEqualBalance) {
      return {
        isValid: false,
        errorMessage: "Insufficient funds",
      }
    }

    return noError
  }

  return {
    depositAmount,
    tokenDecimals,
    depositFromAnotherWallet,
    maxTokenAmount,
    setDepositTokenAmountCallback,
    additionalValidationCallback,
  }
}

export const useDepositBox = ({
  id,
  depositId,
  amount,
  token,
  dailyMats,
}: {
  id: string
  depositId: number | null
  amount: bigint | string
  token: SupportedTokenName
  dailyMats: number
}) => {
  const currentLockForDeposit = useDappSelector((state: RootState) =>
    selectCurrentLockForDeposit(state, id),
  )

  const existingWithdrawal = useDappSelector((state: RootState) =>
    selectWithdrawalById(state, depositId),
  )

  const currentLockDuration = currentLockForDeposit
    ? calculateLockDuration(
        new Date(currentLockForDeposit.lockStart),
        new Date(currentLockForDeposit.lockEnd),
      )
    : 0

  const dispatch = useDappDispatch()

  const formattedDailyMats = formatMats(dailyMats)
  const bigintAmount = typeof amount === "string" ? BigInt(amount) : amount

  const userAmount = bigIntToFloat(bigintAmount, 18, FRIENDLY_DECIMALS_AMOUNT)

  const unlockTime = currentLockForDeposit
    ? getUnlockTime(new Date(currentLockForDeposit.lockEnd).getTime())
    : null

  const openBoostModalHandler = useCallback(() => {
    if (!depositId || !currentLockForDeposit) return

    dispatch(
      initializeLockFlow({
        amount: bigintAmount,
        userAmount,
        lockEnd: new Date(currentLockForDeposit.lockEnd).toISOString(),
        currentLockDuration,
        token,
        dailyMats: formattedDailyMats,
        id,
        depositId,
        actionType: unlockTime ? "extend" : "resume",
      }),
    )

    dispatch(setOpenModal("boost"))
  }, [
    depositId,
    currentLockForDeposit,
    dispatch,
    bigintAmount,
    userAmount,
    currentLockDuration,
    token,
    formattedDailyMats,
    id,
    unlockTime,
  ])

  const openWithdrawalModalHandler = useCallback(() => {
    const shouldOnlyRequestRedemption =
      token === "BTC" && existingWithdrawal
        ? !existingWithdrawal?.redemptionRequested
        : false

    dispatch(
      initializeWithdrawalFlow({
        id,
        depositId,
        amount: bigintAmount,
        userAmount,
        token,
        actionType: shouldOnlyRequestRedemption ? "redemption" : "withdraw",
        dailyMats: formattedDailyMats,
      }),
    )

    dispatch(setOpenModal("withdrawal"))
  }, [
    token,
    existingWithdrawal,
    dispatch,
    id,
    depositId,
    bigintAmount,
    userAmount,
    formattedDailyMats,
  ])

  const openReturnStbtcModalHandler = useCallback(() => {
    dispatch(setDepositId(depositId))
    dispatch(setOpenModal("stbtc-manage", { allowToChangeDeposit: false }))
  }, [dispatch, depositId])

  return {
    openBoostModal: openBoostModalHandler,
    openWithdrawalModal: openWithdrawalModalHandler,
    openReturnStbtcModal: openReturnStbtcModalHandler,
  }
}

export const useDepositCarousel = (
  deposits: DepositForAccount[],
  onChange?: (prevIndex: number, nextIndex: number) => void,
) => {
  const [currentlySelectedDeposit, setCurrentlySelectedDeposit] = useState<
    DepositForAccount | undefined
  >(deposits[0])

  const onDepositCarouselChange = (_: number, nextIndex: number) => {
    if (deposits.length === 0) {
      setCurrentlySelectedDeposit(undefined)
      return
    }

    setCurrentlySelectedDeposit(deposits[nextIndex])

    if (onChange) onChange(_, nextIndex)
  }

  return {
    currentlySelectedDeposit,
    setCurrentlySelectedDeposit,
    onDepositCarouselChange,
  }
}

export const useDepositsTotalUSDValue = () => {
  const activeDeposits = useDappSelector(selectActiveDeposits)
  const statistics = useDappSelector(selectPortalStatistics)
  const { getTokenUsdPrice } = useTokenUsdPrice()

  const depositsValueByToken = useMemo(() => {
    if (!statistics) return 0

    return activeDeposits.reduce((sum, { token, amount }) => {
      const floatAmount = bigIntToFloat(BigInt(amount))

      return (
        sum +
        Number(floatAmount) * getTokenUsdPrice(token as SupportedTokenName)
      )
    }, 0)
  }, [activeDeposits, statistics, getTokenUsdPrice])

  return { totalUSDDepositsValue: depositsValueByToken }
}

export const useUpdateDepositAutoMigration = () => {
  const dispatch = useDispatch()
  const { isBitcoinFlow } = useContext(FlowContext)

  const { address, connector } = useAccount()

  const { mutateAsync, ...rest } = useMutation<
    void,
    Error,
    { depositUuid?: string; isAutoMigrationEnabled: boolean }
  >({
    mutationFn: async ({ depositUuid, isAutoMigrationEnabled }) => {
      let accountAddress: Address | string | undefined = address
      if (isBitcoinFlow && !!connector && isPassportConnector(connector)) {
        // @ts-expect-error we check in `if` whether is a passport connector.
        accountAddress = await connector?.getBitcoinAddress()
      }

      if (!accountAddress || !depositUuid)
        throw new Error("Address or deposit id not available ")

      const url = new URL(
        `accounts/${accountAddress}/deposits/${depositUuid}/auto-migration`,
        API_ENDPOINT,
      )

      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({ isAutoMigrationEnabled }),
      })

      if (!response.ok)
        throw new Error(
          `Failed to update auto migration option for deposit: ${depositUuid}`,
        )
    },
    onSuccess: (_, { depositUuid, isAutoMigrationEnabled }) => {
      dispatch(
        updateDepositAutoMigration({
          depositUuid: depositUuid!,
          isAutoMigrationEnabled,
        }),
      )
    },
  })

  return { updateDepositAutoMigration: mutateAsync, ...rest }
}
